/**
 * Shell-level preferences.
 *
 * ## What is stored, and what is not
 *
 * Three values reach `localStorage`: the colour-theme choice, which synthetic
 * tenant is being previewed, and whether the interface is in reference or demo
 * presentation. All three are display preferences that describe the device, not
 * the person, and none is derived from anything financial.
 *
 * **The amount draft is never stored.** It lives in memory for the lifetime of
 * the view and disappears with it. §12 calls `DRAFT_AMOUNT` client-local and
 * "never a server state"; persisting it locally would quietly turn a transient
 * keystroke into a record of financial intent sitting on the device, which is
 * the sort of thing `CLAUDE.md` § Privacy exists to prevent. Nothing else in
 * this application writes to any storage API.
 */

import { applyTenant, TENANTS, type Tenant, tenantById } from "./tenants.ts";

export type ThemeChoice = "system" | "light" | "dark";

/**
 * How much developer-facing explanation the interface shows.
 *
 * `reference` is the working default: full explanations of what is and is not
 * implemented, including the ones written for engineers. `demo` is the same
 * product with the engineering commentary turned down for showing to a partner.
 *
 * **`demo` never hides that unimplemented behaviour is unimplemented.** The
 * disclosures about bank authorisation and claim links stay on screen in both
 * modes; only their length changes, and the developer notes that explain
 * internals — minor-unit handling, for instance — are the part that goes away.
 * This is presentation hierarchy, not concealment: a demo that let a fake bank
 * approval look real would be the one thing this product cannot ship.
 */
export type PresentationMode = "reference" | "demo";

const THEME_KEY = "nura.theme";
const TENANT_KEY = "nura.tenant";
const PRESENTATION_KEY = "nura.presentation";

function readStored(key: string): string | undefined {
  try {
    return window.localStorage.getItem(key) ?? undefined;
  } catch {
    // Private browsing and embedded webviews can refuse storage entirely. A
    // preference that cannot be remembered is not an error worth surfacing.
    return undefined;
  }
}

function writeStored(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Intentionally ignored, as above.
  }
}

function isThemeChoice(value: string | undefined): value is ThemeChoice {
  return value === "system" || value === "light" || value === "dark";
}

function isPresentationMode(value: string | undefined): value is PresentationMode {
  return value === "reference" || value === "demo";
}

export class ShellState {
  #theme: ThemeChoice;
  #tenant: Tenant;
  #presentation: PresentationMode;
  readonly #listeners = new Set<() => void>();

  constructor() {
    const storedTheme = readStored(THEME_KEY);
    this.#theme = isThemeChoice(storedTheme) ? storedTheme : "system";
    this.#tenant = tenantById(readStored(TENANT_KEY) ?? (TENANTS[0] as Tenant).id);
    const storedPresentation = readStored(PRESENTATION_KEY);
    this.#presentation = isPresentationMode(storedPresentation) ? storedPresentation : "reference";
  }

  get theme(): ThemeChoice {
    return this.#theme;
  }

  get tenant(): Tenant {
    return this.#tenant;
  }

  get presentation(): PresentationMode {
    return this.#presentation;
  }

  /** True when engineering commentary should be turned down. */
  get isDemo(): boolean {
    return this.#presentation === "demo";
  }

  setPresentation(presentation: PresentationMode): void {
    this.#presentation = presentation;
    writeStored(PRESENTATION_KEY, presentation);
    this.apply();
  }

  /** The theme actually in force, resolving "system" against the OS setting. */
  get resolvedTheme(): "light" | "dark" {
    if (this.#theme !== "system") return this.#theme;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }

  setTheme(theme: ThemeChoice): void {
    this.#theme = theme;
    writeStored(THEME_KEY, theme);
    this.apply();
  }

  setTenant(tenant: Tenant): void {
    this.#tenant = tenant;
    writeStored(TENANT_KEY, tenant.id);
    this.apply();
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  /** Push the current preferences into the document and notify subscribers. */
  apply(): void {
    const root = document.documentElement;
    if (this.#theme === "system") root.removeAttribute("data-nura-theme");
    else root.setAttribute("data-nura-theme", this.#theme);

    applyTenant(this.#tenant, this.resolvedTheme);
    for (const listener of this.#listeners) listener();
  }
}
